/**
 * Rewrite Supabase Storage public object URLs to on-the-fly Image Transform
 * (render) URLs. Non-storage URLs (Vite assets, external hosts) pass through.
 *
 * Docs: https://supabase.com/docs/guides/storage/serving/image-transformations
 */

import {
  COMPACT_VENUE_CARD_HEIGHT,
  COMPACT_VENUE_CARD_WIDTH,
} from "@/lib/venueCardStyles";

export type StorageRenderOptions = {
  width: number;
  height?: number;
  /** cover (default) | contain | fill */
  resize?: "cover" | "contain" | "fill";
  /** 20–100, default 75 */
  quality?: number;
};

const OBJECT_PUBLIC = "/storage/v1/object/public/";
const RENDER_PUBLIC = "/storage/v1/render/image/public/";

function supabaseHostAllowed(hostname: string): boolean {
  return hostname.endsWith(".supabase.co");
}

/**
 * Convert a public Storage object URL into a render URL with transform params.
 * Returns null if `src` is not a Supabase public object/render URL we can rewrite.
 */
export function toStorageRenderUrl(
  src: string,
  options: StorageRenderOptions,
): string | null {
  const trimmed = src.trim();
  if (!trimmed || !/^https?:\/\//i.test(trimmed)) return null;

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }

  if (!supabaseHostAllowed(url.hostname)) return null;

  let objectPath: string | null = null;
  if (url.pathname.includes(OBJECT_PUBLIC)) {
    objectPath = url.pathname.slice(
      url.pathname.indexOf(OBJECT_PUBLIC) + OBJECT_PUBLIC.length,
    );
  } else if (url.pathname.includes(RENDER_PUBLIC)) {
    objectPath = url.pathname.slice(
      url.pathname.indexOf(RENDER_PUBLIC) + RENDER_PUBLIC.length,
    );
  }

  if (!objectPath) return null;

  const render = new URL(
    `${url.origin}${RENDER_PUBLIC}${objectPath}`,
  );
  render.searchParams.set("width", String(Math.round(options.width)));
  if (options.height != null) {
    render.searchParams.set("height", String(Math.round(options.height)));
  }
  render.searchParams.set("resize", options.resize ?? "cover");
  render.searchParams.set("quality", String(options.quality ?? 75));
  return render.toString();
}

/** Prefer transformed URL; fall back to original for non-Storage assets. */
export function mediaUrl(src: string, options: StorageRenderOptions): string {
  return toStorageRenderUrl(src, options) ?? src;
}

export type ImagePreset = "thumb" | "card" | "hero";

type PresetConfig = {
  /** Default `src` width (≈1× / 2× CSS target). */
  srcWidth: number;
  /** Candidate widths for srcset (ascending). */
  widths: number[];
  /**
   * CSS box aspect (width/height). Required for `resize=cover` —
   * width-only transforms keep the full source height and barely save bytes.
   */
  aspect: number;
  sizes: string;
  resize: "cover" | "contain" | "fill";
  quality: number;
};

function heightForWidth(width: number, aspect: number): number {
  return Math.max(1, Math.round(width / aspect));
}

const PRESETS: Record<ImagePreset, PresetConfig> = {
  // Circle 48, gallery 88, profile 64–128 — square crop
  thumb: {
    srcWidth: 180,
    widths: [180, 360],
    aspect: 1,
    sizes: "128px",
    resize: "cover",
    quality: 70,
  },
  // Discovery .fcard — COMPACT_VENUE_CARD_* (aspect 230/172)
  card: {
    srcWidth: 400,
    widths: [400, 800],
    aspect: COMPACT_VENUE_CARD_WIDTH / COMPACT_VENUE_CARD_HEIGHT,
    sizes: "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw",
    resize: "cover",
    quality: 75,
  },
  // Detail hero 100vw × 420h (mobile-first box ~430×420) — keep sharp on retina
  hero: {
    srcWidth: 860,
    widths: [860, 1280],
    aspect: 430 / 420,
    sizes: "100vw",
    resize: "cover",
    quality: 80,
  },
};

export function buildPresetSrcSet(
  src: string,
  preset: ImagePreset,
): { src: string; srcSet: string | undefined; sizes: string | undefined } {
  const cfg = PRESETS[preset];
  const transformed = toStorageRenderUrl(src, {
    width: cfg.srcWidth,
    height: heightForWidth(cfg.srcWidth, cfg.aspect),
    resize: cfg.resize,
    quality: cfg.quality,
  });

  // Non-storage (Vite fallback etc.): leave untouched, no srcset.
  if (!transformed) {
    return { src, srcSet: undefined, sizes: undefined };
  }

  const parts: string[] = [];
  for (const w of cfg.widths) {
    const u = toStorageRenderUrl(src, {
      width: w,
      height: heightForWidth(w, cfg.aspect),
      resize: cfg.resize,
      quality: cfg.quality,
    });
    if (u) parts.push(`${u} ${w}w`);
  }

  return {
    src: transformed,
    srcSet: parts.length > 0 ? parts.join(", ") : undefined,
    sizes: cfg.sizes,
  };
}
